/**
 * Attack Surface Cell Renderer
 *
 * Renders the attack surface tags for an asset using the AttackSurfaceComponent.
 * Automatically prioritizes filtered attack surface values when filters are active.
 */

import React from 'react';

import { useCellFilters } from '@/sections/asset/components/cells/CellFiltersContext';
import { AttackSurfaceComponent } from '@/sections/asset/components/tabs';
import type { Asset } from '@/types';

interface AttackSurfaceCellProps {
  asset: Asset;
}

/**
 * Memoized cell renderer for attack surface column to improve performance
 *
 * Features:
 * - Shows first attack surface chip with "+N" counter for additional values
 * - Hovering anywhere in the cell shows all attack surface values in tooltip
 * - Filtered values appear first when attack surface filters are active
 * - Matches the interaction pattern of the origins column
 *
 * @param asset - The asset to render attack surface for
 * @returns AttackSurfaceComponent with configured display settings
 */
export const AttackSurfaceCell: React.FC<AttackSurfaceCellProps> = React.memo(
  ({ asset }) => {
    // Access filter context for intelligent chip prioritization
    const filters = useCellFilters();
    const activeAttackSurfaceFilters = filters?.advancedFilters?.attackSurface;

    return (
      <AttackSurfaceComponent
        asset={asset}
        maxVisible={1} // Show first chip with "+N" counter
        size="xs"
        wrapInTooltip // Hover anywhere in cell to see all values
        activeFilters={activeAttackSurfaceFilters} // Prioritize filtered values
      />
    );
  }
);

AttackSurfaceCell.displayName = 'AttackSurfaceCell';
